import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  getBlob,
} from "firebase/storage";
import { db, storage, TASK_STATUS } from "../firebase.config.js";
import { useAgency } from '../AgencyContext.js';
import { useTaskTracker } from '../TaskTrack/useTaskTracker';
import "./L2Component.css";
import { Download, FileUpload, Info } from "@mui/icons-material";

// Cache pour les images préchargées
const imageCache = new Map();
const thumbnailCache = new Map();

const L2Component = ({ task, taskId, user, userRole, onTaskUpdate }) => {
  const { getAgencyDoc, currentAgency } = useAgency();
  const { trackEvent } = useTaskTracker();
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [preloadedImages, setPreloadedImages] = useState(new Set());
  const [thumbnails, setThumbnails] = useState(new Map());
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Check if user is assigned to this task
  const isAssignedToUser = task.assignedTo === user.email || userRole === 'Manager';

  // Get L1 and L2 images
  const l1Images = useMemo(
    () =>
      task.images?.filter((img) => !img.isL2Image && img.status === "approved") ||
      [],
    [task.images]
  );
  const l2Images = useMemo(
    () => task.images?.filter((img) => img.isL2Image) || [],
    [task.images]
  );

  const currentImageCount = l2Images.length;
  const remainingImages = task.quantity - currentImageCount;
  const canUpload = currentImageCount < task.quantity && isAssignedToUser;

  // Calculate stats for L2 images
  const rejectedCount = l2Images.filter(
    (img) => img.status === "rejected"
  ).length;
  const approvedCount = l2Images.filter(
    (img) => img.status === "approved"
  ).length;
  const isAllUploaded = currentImageCount === task.quantity;
  const canComplete = isAllUploaded && isAssignedToUser && rejectedCount === 0;

  // Check if there are QA comments to display - task-level comments from QA
  const qaComments = task.comments?.filter(comment => 
    comment.status && (
      comment.status.includes('TO_BE_REWORKED_L2') || 
      comment.status.includes('L2')
    )
  ).slice(-1) || [];

  // Créer des thumbnails pour les previews rapides
  const createThumbnail = useCallback((file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        const maxSize = 150;
        let { width, height } = img;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };

      img.src = URL.createObjectURL(file);
    });
  }, []);

  const createVideoThumbnail = useCallback((file) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      video.addEventListener('loadedmetadata', () => {
        video.currentTime = 1; // Capture at 1 second
      });

      video.addEventListener('seeked', () => {
        const maxSize = 150;
        let { videoWidth: width, videoHeight: height } = video;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(video, 0, 0, width, height);
        
        // Add play icon overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.beginPath();
        ctx.arc(width / 2, height / 2, 20, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.moveTo(width / 2 - 8, height / 2 - 10);
        ctx.lineTo(width / 2 + 10, height / 2);
        ctx.lineTo(width / 2 - 8, height / 2 + 10);
        ctx.closePath();
        ctx.fill();
        
        resolve(canvas.toDataURL("image/jpeg", 0.7));
        URL.revokeObjectURL(video.src);
      });

      video.src = URL.createObjectURL(file);
    });
  }, []);

  // Précharger toutes les images en arrière-plan
  const preloadImages = useCallback(async () => {
    if (!l2Images) return;

    l2Images.forEach(async (image) => {
      if (!imageCache.has(image.id) && !preloadedImages.has(image.id)) {
        try {
          const img = new Image();
          img.crossOrigin = "anonymous";

          img.onload = () => {
            imageCache.set(image.id, img);
            setPreloadedImages((prev) => new Set([...prev, image.id]));
          };

          img.onerror = () => {
            console.warn(`Failed to preload image: ${image.id}`);
          };

          img.src = image.url;
        } catch (error) {
          console.warn(`Error preloading image ${image.id}:`, error);
        }
      }
    });
  }, [l2Images, preloadedImages]);

  // Précharger les images au montage et quand elles changent
  useEffect(() => {
    preloadImages();
  }, [preloadImages]);

  // Auto-select first image when images are loaded
  useEffect(() => {
    if (l2Images && l2Images.length > 0 && !selectedImage) {
      const sortedImages = [...l2Images].sort((a, b) => {
        const statusOrder = { rejected: 0, pending: 1, approved: 2 };
        return (
          statusOrder[a.status || "pending"] -
          statusOrder[b.status || "pending"]
        );
      });
      setSelectedImage(sortedImages[0]);
    }
  }, [l2Images, selectedImage]);


  // Mémoiser les images triées pour éviter les re-calculs
  const sortedImages = useMemo(() => {
    if (!l2Images) return [];
    return [...l2Images].sort((a, b) => {
      const statusOrder = { rejected: 0, pending: 1, approved: 2 };
      return (
        statusOrder[a.status || "pending"] - statusOrder[b.status || "pending"]
      );
    });
  }, [l2Images]);

  // Fonction pour générer un nom aléatoire au format AI_FACTORY_XXXXXXX
  const generateRandomFileName = () => {
    const randomNumber = Math.floor(Math.random() * 10000000); // Génère un nombre de 0 à 9999999
    const paddedNumber = randomNumber.toString().padStart(7, '0'); // Assure 7 chiffres avec des zéros au début
    return `AI_FACTORY_${paddedNumber}`;
  };

  const generateZipFileName = () => {
    const taskName =
      task.taskName?.replace(/[^a-z0-9]/gi, "_").toLowerCase() || "task";
    const date = new Date().toISOString().split("T")[0];
    return `${taskName}_L1_${l1Images.length}_images_${date}.zip`;
  };

  const downloadL1ImagesAsZip = async () => {
    if (isDownloading || l1Images.length === 0) return;

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();

      for (let i = 0; i < l1Images.length; i++) {
        const image = l1Images[i];

        try {
          setDownloadProgress(Math.round(((i + 1) / l1Images.length) * 90));

          let storagePath;
          if (image.path) {
            storagePath = image.path;
          } else if (image.fileName && taskId) {
            storagePath = `tasks/${taskId}/${image.fileName}`;
          } else if (image.name && taskId) {
            // Fallback pour les anciennes images - essayer de deviner l'extension depuis l'originalName
            const extension = image.originalName ? image.originalName.split('.').pop() : 'jpg';
            storagePath = `tasks/${taskId}/${image.name}.${extension}`;
          } else {
            try {
              const urlParts = image.url.split("/o/")[1];
              storagePath = decodeURIComponent(urlParts.split("?")[0]);
            } catch (e) {
              console.error(`Cannot extract path for ${image.name}`);
              continue;
            }
          }

          const storageRef = ref(storage, storagePath);
          const blob = await getBlob(storageRef);
          // Utiliser fileName (avec extension) ou originalName comme fallback
          const filename = image.fileName || image.originalName || `image_${i + 1}.jpg`;
          zip.file(filename, blob);
        } catch (error) {
          console.error(`Error downloading ${image.name}:`, error);
        }
      }

      setDownloadProgress(95);
      const content = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = generateZipFileName();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(url), 100);
      setDownloadProgress(100);

    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const simulateUploadProgress = (fileName, duration = 3000) => {
    const steps = 20;
    const stepDuration = duration / steps;
    let currentStep = 0;

    const interval = setInterval(() => {
      currentStep++;
      const progressValue = Math.min(95, (currentStep / steps) * 100);

      setUploadProgress((prev) => ({
        ...prev,
        [fileName]: progressValue,
      }));

      if (currentStep >= steps) {
        clearInterval(interval);
      }
    }, stepDuration);

    return interval;
  };

  const handleFileUpload = async (files) => {
    if (!canUpload) return;
    if (!files || files.length === 0) return;
    if (files.length > remainingImages) return;

    setUploading(true);
    setUploadProgress({});
    const uploadedImages = [];
    const errors = [];
    const progressIntervals = {};

    try {
      // Créer les thumbnails en parallèle pour les previews immédiates
      const thumbnailPromises = Array.from(files).map(async (file) => {
        if (file.type.startsWith("image/")) {
          const thumbnail = await createThumbnail(file);
          setThumbnails((prev) => new Map([...prev, [file.name, thumbnail]]));
        } else if (file.type.startsWith("video/")) {
          // For videos, create a video thumbnail
          const videoThumbnail = await createVideoThumbnail(file);
          setThumbnails((prev) => new Map([...prev, [file.name, videoThumbnail]]));
        }
      });

      await Promise.all(thumbnailPromises);

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const isImage = file.type.startsWith("image/");
        const isVideo = file.type.startsWith("video/");
        
        if (!isImage && !isVideo) {
          errors.push(`${file.name} is not a valid image or video`);
          continue;
        }

        const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB for videos, 10MB for images
        if (file.size > maxSize) {
          errors.push(`${file.name} is too large (max ${isVideo ? '50MB' : '10MB'})`);
          continue;
        }

        try {
          progressIntervals[file.name] = simulateUploadProgress(file.name);

          // Générer un nom aléatoire au format AI_FACTORY_XXXXXXX avec l'extension du fichier original
          const fileExtension = file.name.split('.').pop().toLowerCase();
          const randomName = generateRandomFileName();
          const fileName = `L2_${randomName}.${fileExtension}`;

          const storageRef = ref(storage, `tasks/${taskId}/${fileName}`);
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);

          setUploadProgress((prev) => ({
            ...prev,
            [file.name]: 100,
          }));

          const mediaData = {
            id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: randomName, // Utiliser le nom généré au lieu du nom original
            originalName: file.name, // Garder le nom original pour référence
            fileName: fileName,
            url: downloadURL,
            path: snapshot.ref.fullPath,
            uploadedBy: user.email,
            uploadedAt: new Date(),
            status: "pending",
            qaComment: "",
            size: file.size,
            type: file.type,
            mediaType: isVideo ? 'video' : 'image', // New field to distinguish media types
            isL2Image: true,
          };

          uploadedImages.push(mediaData);

          // Précharger immédiatement la nouvelle image
          // Only preload images, not videos
          if (isImage) {
            const img = new Image();
            img.crossOrigin = "anonymous";
            img.onload = () => {
              imageCache.set(mediaData.id, img);
              setPreloadedImages((prev) => new Set([...prev, mediaData.id]));
            };
            img.src = downloadURL;
          }
        } catch (fileError) {
          console.error(`Error for ${file.name}:`, fileError);
          errors.push(`Error uploading ${file.name}`);

          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[file.name];
            return newProgress;
          });
        }
      }

      Object.values(progressIntervals).forEach((interval) =>
        clearInterval(interval)
      );

      if (uploadedImages.length > 0) {
        const taskDocRef = getAgencyDoc('taskbacklog', taskId);
        if (!taskDocRef) {
          console.error("Could not get agency document reference for L2 upload");
          return;
        }
        
        await updateDoc(taskDocRef, {
          images: arrayUnion(...uploadedImages),
          lastModified: serverTimestamp(),
          lastModifiedBy: user.email,
        });

        // Track the image upload
        const imageCount = uploadedImages.length;
        const imageText = imageCount === 1 ? 'media' : 'media';
        await trackEvent(taskId, task.taskName, `uploaded ${imageCount} ${imageText}`, currentAgency?.userRole || 'L2', task.quantity);

        await onTaskUpdate();
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      setUploadProgress({});
      // Nettoyer les thumbnails temporaires
      setThumbnails(new Map());
    }
  };

  // Optimiser la suppression avec mise à jour immédiate de l'UI
  const handleImageDelete = useCallback(
    async (imageToDelete) => {
      if (!isAssignedToUser) {
        alert("You must assign this task to yourself before deleting images.");
        return;
      }

      if (!imageToDelete.isL2Image) {
        alert("You can only delete L2 images");
        return;
      }


      try {
        // Mise à jour immédiate de l'UI (optimistic update)
        const updatedImages = l2Images.filter(
          (img) => img.id !== imageToDelete.id
        );

        // Si l'image sélectionnée est supprimée, sélectionner la suivante immédiatement
        if (selectedImage?.id === imageToDelete.id) {
          if (updatedImages.length > 0) {
            setSelectedImage(updatedImages[0]);
          } else {
            setSelectedImage(null);
          }
        }

        // Nettoyer le cache
        imageCache.delete(imageToDelete.id);
        setPreloadedImages((prev) => {
          const newSet = new Set(prev);
          newSet.delete(imageToDelete.id);
          return newSet;
        });

        // Opérations Firebase en arrière-plan
        const taskDocRef = getAgencyDoc('taskbacklog', taskId);
        if (!taskDocRef) {
          console.error("Could not get agency document reference for L2 delete");
          return;
        }
        
        const deletePromises = [
          deleteObject(ref(storage, imageToDelete.path)),
          updateDoc(taskDocRef, {
            images: arrayRemove(imageToDelete),
            lastModified: serverTimestamp(),
            lastModifiedBy: user.email,
          }),
        ];

        await Promise.all(deletePromises);
        await onTaskUpdate();
      } catch (error) {
        console.error("Delete error:", error);
      }
    },
    [
      l2Images,
      selectedImage,
      taskId,
      user.email,
      onTaskUpdate,
      isAssignedToUser,
    ]
  );

  // Optimiser la sélection d'image
  const handleImageSelect = useCallback(
    (image) => {
      if (!isAssignedToUser) {
        alert(
          "You must assign this task to yourself before you can interact with it."
        );
        return;
      }
      setSelectedImage(image);

      // Si l'image n'est pas encore préchargée, la charger immédiatement
      if (!imageCache.has(image.id)) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          imageCache.set(image.id, img);
          setPreloadedImages((prev) => new Set([...prev, image.id]));
        };
        img.src = image.url;
      }
    },
    [isAssignedToUser]
  );

  const handleCompleteTask = async () => {
    if (!canComplete) return;

    try {
      const taskDocRef = getAgencyDoc('taskbacklog', taskId);
      if (!taskDocRef) {
        console.error("Could not get agency document reference for L2 completion");
        return;
      }

      // Count L2 images uploaded by current user
      const l2Images = task.images?.filter(img => img.isL2Image && img.uploadedBy === user.email) || [];
      const totalL2Images = l2Images.length;

      // Update leaderboard stats for L2 user
      const updateData = {
        status: TASK_STATUS.PENDING_QA_L2,
        statusChangedAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email,
        assignedTo: null,
        assignedAt: null,
      };

      // Track L2 user stats for this step
      if (totalL2Images > 0) {
        const leaderboardStats = task.leaderboardStats || {};
        
        if (!leaderboardStats[user.email]) {
          leaderboardStats[user.email] = {
            role: 'L2',
            tasksCompleted: 0,
            imagesSubmitted: 0,
            imagesAccepted: 0,
            imagesRejected: 0,
            submissions: [],
            manager: task.createdBy || task.taskOwner || 'unknown'
          };
        }

        // Track this submission
        leaderboardStats[user.email].tasksCompleted += 1;
        leaderboardStats[user.email].imagesSubmitted += totalL2Images;
        leaderboardStats[user.email].submissions.push({
          timestamp: new Date(),
          phase: 'L2_TO_QA',
          imagesCount: totalL2Images,
          taskId: taskId
        });

        updateData.leaderboardStats = leaderboardStats;
      }

      await updateDoc(taskDocRef, updateData);

      // Track sending to QA
      await trackEvent(taskId, task.taskName, 'sent to QA', currentAgency?.userRole || 'L2', task.quantity);

      window.location.href = "/backlog";
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleUploadClick = () => {
    if (!isAssignedToUser) {
      alert("You must assign this task to yourself before uploading files.");
      return;
    }
    fileInputRef.current?.click();
  };

  const getImageStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "l2__image-badge--approved";
      case "rejected":
        return "l2__image-badge--rejected";
      default:
        return "l2__image-badge--pending";
    }
  };

  // Composant optimisé pour l'image de prévisualisation
  const PreviewMedia = ({ media }) => {
    const isVideo = media.mediaType === 'video' || media.type?.startsWith('video/');
    const [videoLoaded, setVideoLoaded] = useState(false);
    
    if (isVideo) {
      // Affichage vidéo avec état de chargement
      return (
        <div className="l2__preview-container">
          {!videoLoaded && (
            <div className="l2__video-loading">
              <div className="l2__loading-text">Loading video...</div>
            </div>
          )}
          <video
            src={media.url}
            className="l2__preview-video"
            controls
            preload="metadata"
            style={{ opacity: videoLoaded ? 1 : 0 }}
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoLoaded(true)} // Show video element even on error
          >
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    // Logique existante pour les images
    const cachedImage = imageCache.get(media.id);

    if (cachedImage) {
      // Image déjà en cache, affichage instantané
      return (
        <img
          src={media.url}
          alt={media.name}
          className="l2__preview-image l2__preview-image--cached"
          style={{ opacity: 1 }}
        />
      );
    }

    // Image pas encore chargée, utiliser une version optimisée
    return (
      <div className="l2__preview-container">
        <img
          src={media.url}
          alt={media.name}
          className="l2__preview-image l2__preview-image--loading"
          loading="eager"
          decoding="async"
          onLoad={() => {
            const img = new Image();
            img.src = media.url;
            imageCache.set(media.id, img);
            setPreloadedImages((prev) => new Set([...prev, media.id]));
          }}
        />
      </div>
    );
  };

  return (
    <div className="l2">
      <div className="l2__content">
        {/* Left Panel */}
        <div className="l2__left-panel">
          {/* Task Info Card */}
          <div className="l2__task-info-card" title={`${task.taskName}${task.description ? '\n\n' + task.description : ''}`}>
            <div className="l2__task-title">
              {task.taskName && task.taskName.length > 30 
                ? `${task.taskName.substring(0, 30)}...` 
                : task.taskName
              }
            </div>
            {task.description && (
              <div className="l2__task-description">
                {task.description.length > 60 
                  ? `${task.description.substring(0, 60)}...` 
                  : task.description
                }
              </div>
            )}
          </div>
          {/* Upload Button */}
          <button
            className={`l2__upload-btn ${
              !canUpload ? "l2__upload-btn--disabled" : ""
            }`}
            onClick={handleUploadClick}
            disabled={!canUpload || !isAssignedToUser}
          >
            <span className="l2__upload-icon">
              <FileUpload />
            </span>{" "}
            Upload L2 · {currentImageCount}/{task.quantity}
          </button>

          {/* QA Comments */}
          {qaComments.length > 0 && (
            <div className="l2__qa-comments">
              {qaComments.map((comment, index) => (
                <div key={index} className="l2__qa-comment">
                  <div className="l2__qa-comment-header">
                  {comment.author || "QA Team"}
                  </div>
                  <div className="l2__qa-comment-text">{comment.text}</div>
                </div>
              ))}
            </div>
          )}

          {/* Rejection Warning */}
          {rejectedCount > 0 && (
            <div className="l2__rejection__warning">
              <div className="l2__rejection__warning__icon">
                <Info style={{ scale: 0.7 }} />
              </div>

              <div className="l2__rejection__warning__txt">
                <p>Delete and reupload content</p>
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && Object.keys(uploadProgress).length > 0 && (
            <div className="l2__upload-progress">
              {Object.entries(uploadProgress).map(([fileName, progress]) => (
                <div key={fileName} className="l2__upload-progress-item">
                  <div
                    className="l2__upload-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Image List */}
          {sortedImages.length > 0 && (
            <div className="l2__image-list">
              {sortedImages.map((image) => (
                <div key={image.id} className="l2__image-item">
                  <div
                    className={`l2__image-badge ${getImageStatusClass(
                      image.status
                    )} ${
                      selectedImage?.id === image.id
                        ? "l2__image-badge--selected"
                        : ""
                    } ${
                      preloadedImages.has(image.id)
                        ? "l2__image-badge--loaded"
                        : ""
                    }`}
                    onClick={() => handleImageSelect(image)}
                  >
                    <span className="l2__image-badge-name">{image.name}</span>

                    {image.status !== "approved" && (
                      <button
                        className="l2__image-badge-close"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageDelete(image);
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  
                  {/* Show rejection comment if image is rejected */}
                  {image.status === "rejected" && image.qaComment && (
                    <div className="l2__rejection-comment">
                      <div className="l2__rejection-comment-text">{image.qaComment}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFileUpload(Array.from(e.target.files));
                e.target.value = "";
              }
            }}
            style={{ display: "none" }}
          />
        </div>

        {/* Center Panel */}
        <div
          className={`l2__center-panel ${
            selectedImage ? "l2__center-panel--with-image" : ""
          }`}
        >
          {selectedImage ? (
            <PreviewMedia media={selectedImage} />
          ) : (
            <div className="l2__center-placeholder">Upload L2 images</div>
          )}

          <div className="CTA__download">
            {/* Download L1 Button - positioned like Generate in L1 */}
            <button
              onClick={downloadL1ImagesAsZip}
              className={`l2__download-btn ${
                isDownloading ? "l2__download-btn--downloading" : ""
              }`}
              disabled={l1Images.length === 0 || !isAssignedToUser}
            >
              <span className="l2__download-icon">
                <Download />
              </span>
              {isDownloading
                ? `Downloading ${downloadProgress}%`
                : `Download ${l1Images.length}`}
            </button>

            <button
              className={`l2__send-qa-btn ${
                canComplete
                  ? "l2__send-qa-btn--active"
                  : "l2__send-qa-btn--disabled"
              }`}
              onClick={handleCompleteTask}
              disabled={!canComplete}
            >
              Send to QA
            </button>
          </div>
        </div>
      </div>

      {/* Assignment Warning Overlay */}
      {!isAssignedToUser && (
        <div className="l2__assignment-overlay">
          <div className="l2__assignment-warning">
            <div className="l2__warning-icon">🔒</div>
            <h3>Task Not Assigned</h3>
            <p>
              You must assign this task to yourself using the "Take Task" button
              in the header before you can work on it.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default L2Component;
